import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { authPostRequest } from "../../api/authClient";

const MIN_PASSWORD_LEN = 8;

function signInHref(): string {
  return "/";
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get("token") || "").trim(), [searchParams]);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!token) {
      setError("Missing reset token. Open the link from your email or request a new one.");
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LEN) {
      setError(`Password must be at least ${MIN_PASSWORD_LEN} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      await authPostRequest("/member/auth/reset-password", {
        token,
        new_password: newPassword,
      });
      setMessage("Password updated. Redirecting to sign in…");
      setTimeout(() => {
        window.location.href = signInHref();
      }, 1500);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as { message?: string })?.message;
      setError(typeof msg === "string" && msg.length > 0 ? msg : "Could not reset password. The link may have expired.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-slate-50">Set new password</h2>
        <p className="text-sm text-slate-400 mt-1">Choose a password you have not used here before.</p>
      </div>

      {!token ? (
        <div className="rounded-lg border border-amber-800/60 bg-amber-950/40 px-4 py-3 text-sm text-amber-100">
          This page needs a valid token from your email.{" "}
          <Link to="/forgot-password" className="font-medium text-sky-400 hover:text-sky-300">
            Request a reset link
          </Link>
          .
        </div>
      ) : null}

      <form className="space-y-4" onSubmit={onSubmit}>
        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label htmlFor="surv-reset-new" className="text-xs font-medium text-slate-300">
              New password
            </label>
            <button
              type="button"
              onClick={() => setShowNew((s) => !s)}
              className="text-xs text-sky-400 hover:text-sky-300"
            >
              {showNew ? "Hide" : "Show"}
            </button>
          </div>
          <input
            id="surv-reset-new"
            type={showNew ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={MIN_PASSWORD_LEN}
            autoComplete="new-password"
            disabled={!token}
            className="w-full rounded-lg border border-slate-700 bg-slate-950/50 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:opacity-50"
            placeholder={`At least ${MIN_PASSWORD_LEN} characters`}
          />
        </div>

        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label htmlFor="surv-reset-confirm" className="text-xs font-medium text-slate-300">
              Confirm password
            </label>
            <button
              type="button"
              onClick={() => setShowConfirm((s) => !s)}
              className="text-xs text-sky-400 hover:text-sky-300"
            >
              {showConfirm ? "Hide" : "Show"}
            </button>
          </div>
          <input
            id="surv-reset-confirm"
            type={showConfirm ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={MIN_PASSWORD_LEN}
            autoComplete="new-password"
            disabled={!token}
            className="w-full rounded-lg border border-slate-700 bg-slate-950/50 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:opacity-50"
            placeholder="Re-enter password"
          />
        </div>

        {error ? <p className="text-sm text-red-400">{error}</p> : null}
        {message ? <p className="text-sm text-emerald-400">{message}</p> : null}

        <button
          type="submit"
          disabled={isLoading || !token}
          className="w-full rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? "Updating…" : "Update password"}
        </button>
      </form>

      <p className="text-center text-sm text-slate-400">
        <a href={signInHref()} className="font-medium text-sky-400 hover:text-sky-300">
          Back to sign in
        </a>
      </p>
    </div>
  );
}
